import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowUpDown,
  FileText,
  Image as ImageIcon,
  Info,
  Library,
  Pencil,
  Plus,
  RefreshCw,
  Upload,
  X,
} from "lucide-react";
import {
  researchProductsAdminQueryOptions,
  shopifyAdminStatusQueryOptions,
  formatPrice,
  formatPublishDate,
  publishTime,
  type ProductFileKind,
  type ProductStatus,
  type ResearchProductAdmin,
} from "@/lib/store.queries";
import {
  archiveProduct,
  attachProductFile,
  checkProductFile,
  detachProductFile,
  saveProduct,
  syncProducts,
  SHOPIFY_ADMIN_UNCONFIGURED,
} from "@/lib/store.functions";
import { ApiError } from "@/lib/api";
import { MEDIA_TARGETS } from "@/lib/media-buckets";
import { useMediaUpload } from "@/hooks/useMediaUpload";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { MediaPickerDialog } from "@/components/admin/MediaPickerDialog";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";

export const Route = createFileRoute("/_authenticated/admin/research")({
  component: ResearchAdmin,
});

const WAITING_MESSAGE =
  "Waiting for the Shopify Admin token from the customer — product editing is disabled. " +
  "Product sync from Shopify still works.";

/** Shopify ingests uploaded files asynchronously; we poll until it says READY. */
const FILE_POLL_MS = 4000;
const FILE_POLL_TIMEOUT_MS = 2 * 60_000;

type FormState = {
  id?: string;
  title: string;
  /** Dollars as typed, converted to cents on save. */
  price: string;
  status: ProductStatus;
  publish_date: string;
  pages: string;
  /** One detail per line. */
  details: string;
  details_description: string;
  description_html: string;
};

const emptyForm: FormState = {
  title: "",
  price: "",
  status: "DRAFT",
  publish_date: "",
  pages: "",
  details: "",
  details_description: "",
  description_html: "",
};

type SortKey = "date-desc" | "date-asc" | "title-asc" | "title-desc";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "date-desc", label: "Newest first" },
  { value: "date-asc", label: "Oldest first" },
  { value: "title-asc", label: "Title A to Z" },
  { value: "title-desc", label: "Title Z to A" },
];

/**
 * Date sorts use publishTime() from store.queries (shared with the public
 * pages); legacy free text it can't read returns null and sorts last either way.
 */
function sortProducts(rows: ResearchProductAdmin[], sortBy: SortKey): ResearchProductAdmin[] {
  const byTitle = (a: ResearchProductAdmin, b: ResearchProductAdmin) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: "base", numeric: true });
  if (sortBy === "title-asc") return [...rows].sort(byTitle);
  if (sortBy === "title-desc") return [...rows].sort((a, b) => byTitle(b, a));
  const dir = sortBy === "date-desc" ? -1 : 1;
  return [...rows]
    .map((p) => ({ p, t: publishTime(p.publish_date) }))
    .sort((a, b) => {
      if (a.t === null || b.t === null) {
        if (a.t !== b.t) return a.t === null ? 1 : -1;
      } else if (a.t !== b.t) {
        return (a.t - b.t) * dir;
      }
      return byTitle(a.p, b.p);
    })
    .map(({ p }) => p);
}

function formOf(p: ResearchProductAdmin): FormState {
  return {
    id: p.id,
    title: p.title,
    price: (p.price_cents / 100).toFixed(2),
    status: p.status ?? (p.is_active ? "ACTIVE" : "ARCHIVED"),
    publish_date: p.publish_date ?? "",
    pages: p.pages == null ? "" : String(p.pages),
    details: (p.details ?? []).join("\n"),
    details_description: p.details_description ?? "",
    description_html: p.description_html ?? "",
  };
}

function ResearchAdmin() {
  const { data: rows = [], isLoading } = useQuery(researchProductsAdminQueryOptions());
  const { data: adminStatus } = useQuery(shopifyAdminStatusQueryOptions());
  const qc = useQueryClient();

  const canEdit = adminStatus?.configured === true;

  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"ALL" | ProductStatus>("ALL");
  const [sortBy, setSortBy] = useState<SortKey>("date-desc");
  const [form, setForm] = useState<FormState>(emptyForm);
  /**
   * The values the dialog was seeded with. Save compares against these so an
   * untouched field is omitted from the payload rather than written back — the
   * row is a cache of Shopify, and echoing a cached blank would clear whatever
   * the cache failed to mirror.
   */
  const [initialForm, setInitialForm] = useState<FormState>(emptyForm);
  /** The saved row behind the open dialog — source for the cover/PDF previews. */
  const [editing, setEditing] = useState<ResearchProductAdmin | null>(null);
  const [busyKind, setBusyKind] = useState<ProductFileKind | null>(null);
  const [pickerKind, setPickerKind] = useState<ProductFileKind | null>(null);
  const coverRef = useRef<HTMLInputElement>(null);
  const pdfRef = useRef<HTMLInputElement>(null);
  // Staging uploads on our media tree (type/size rules + toasts shared with
  // every other uploader); Shopify then pulls the file from the public URL.
  const coverUpload = useMediaUpload(MEDIA_TARGETS.researchCover);
  const pdfUpload = useMediaUpload(MEDIA_TARGETS.researchPdf);

  const sorted = useMemo(() => sortProducts(rows, sortBy), [rows, sortBy]);

  /**
   * A row synced before migration 014 has status NULL — infer it the same way
   * the edit dialog does, so the filter never hides a row the badge shows.
   */
  const effectiveStatus = (p: ResearchProductAdmin): ProductStatus =>
    p.status ?? (p.is_active ? "ACTIVE" : "ARCHIVED");

  const counts = useMemo(() => {
    const c = { ALL: rows.length, ACTIVE: 0, DRAFT: 0, ARCHIVED: 0 };
    for (const p of rows) c[effectiveStatus(p)] += 1;
    return c;
  }, [rows]);

  const visible = useMemo(
    () => (statusFilter === "ALL" ? sorted : sorted.filter((p) => effectiveStatus(p) === statusFilter)),
    [sorted, statusFilter],
  );

  /** Seed the working copy and the baseline that Save diffs against. */
  const seedForm = (f: FormState) => {
    setForm(f);
    setInitialForm(f);
  };

  const syncMut = useMutation({
    mutationFn: () => syncProducts(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["research_products"] });
      toast.success(
        `Synced ${res.synced} product${res.synced === 1 ? "" : "s"}` +
          (res.deactivated > 0 ? ` — ${res.deactivated} deactivated` : ""),
      );
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Sync failed"),
  });

  /** The token can go missing mid-session; every write funnels its errors here. */
  const reportWriteError = (e: unknown, fallback: string) => {
    if (e instanceof ApiError && e.code === SHOPIFY_ADMIN_UNCONFIGURED) {
      qc.invalidateQueries({ queryKey: ["shopify-admin-status"] });
      toast.error(WAITING_MESSAGE);
      return;
    }
    toast.error(e instanceof Error ? e.message : fallback);
  };

  const saveMut = useMutation({
    mutationFn: async (f: FormState) => {
      const dollars = Number(f.price);
      if (!Number.isFinite(dollars) || dollars < 0) throw new Error("Enter a valid price");
      const details = f.details
        .split("\n")
        .map((d) => d.trim())
        .filter(Boolean);
      // A create has nothing to preserve, so it sends everything; an edit
      // sends only what changed. A blank in a touched field travels as "" and
      // clears; an untouched field is omitted and left alone.
      const touched = (k: keyof FormState) => !f.id || f[k] !== initialForm[k];
      // Status is the same three-state case. The select's fallback exists only
      // to give the badge something to show; a row whose status column is
      // still NULL has a status we have never observed — most likely DRAFT,
      // since a draft drops out of the Storefront sync and deactivates the
      // row — so that displayed guess must not travel as an instruction.
      const statusObserved = editing?.status != null;
      const sendStatus = !f.id || statusObserved || touched("status");
      return saveProduct({
        data: {
          id: f.id,
          title: f.title.trim(),
          price_cents: Math.round(dollars * 100),
          ...(sendStatus ? { status: f.status } : {}),
          ...(touched("description_html") ? { description_html: f.description_html } : {}),
          ...(touched("pages") ? { pages: f.pages === "" ? null : Number(f.pages) } : {}),
          ...(touched("publish_date") ? { publish_date: f.publish_date.trim() } : {}),
          ...(touched("details") ? { details } : {}),
          ...(touched("details_description")
            ? { details_description: f.details_description.trim() }
            : {}),
        },
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["research_products"] });
      // The dialog stays open: a new product has no Shopify id until this
      // point, and the cover and PDF can only attach to a saved product.
      if (res.product) {
        setEditing(res.product);
        seedForm(formOf(res.product));
        toast.success("Saved to Shopify");
        return;
      }
      // The re-sync writes the row before reading it back, so an empty
      // envelope is not expected — close rather than show a stale product.
      setOpen(false);
      toast.success("Saved to Shopify — reopen the report to attach files");
    },
    onError: (e) => reportWriteError(e, "Save failed"),
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveProduct({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["research_products"] });
      setOpen(false);
      toast.success("Product archived");
    },
    onError: (e) => reportWriteError(e, "Archive failed"),
  });

  /**
   * Hand Shopify a URL on our own public media tree, then poll until it has
   * pulled the bytes down. Shopify accepts the file asynchronously, so a
   * successful attach call only means the job started.
   */
  async function attachAndPoll(kind: ProductFileKind, sourceUrl: string) {
    const productId = editing?.id;
    if (!productId) return;
    const noun = kind === "cover" ? "Cover image" : "Summary PDF";
    setBusyKind(kind);
    const t = toast.loading(`Sending the ${noun.toLowerCase()} to Shopify…`);
    try {
      const { fileId } = await attachProductFile({ data: { productId, kind, sourceUrl } });
      toast.loading("Shopify is processing the file…", { id: t });
      const startedAt = Date.now();
      for (;;) {
        await new Promise((r) => setTimeout(r, FILE_POLL_MS));
        const res = await checkProductFile({ data: { fileId, productId, kind } });
        if (res.status === "READY") {
          qc.invalidateQueries({ queryKey: ["research_products"] });
          if (res.product) setEditing(res.product);
          toast.success(`${noun} attached`, { id: t });
          return;
        }
        if (res.status === "FAILED") {
          toast.error(
            `Shopify rejected the file${res.errors.length ? `: ${res.errors.join(", ")}` : ""}`,
            { id: t },
          );
          return;
        }
        if (Date.now() - startedAt > FILE_POLL_TIMEOUT_MS) {
          toast.info("Still processing — it will finish in the background; reopen this product later", {
            id: t,
          });
          qc.invalidateQueries({ queryKey: ["research_products"] });
          return;
        }
      }
    } catch (e) {
      toast.dismiss(t);
      reportWriteError(e, `${noun} upload failed`);
    } finally {
      setBusyKind(null);
    }
  }

  /**
   * Drop a cover or PDF reference. Reversible — the file stays in Shopify
   * Files, so re-attaching it restores the link.
   */
  async function detachFile(kind: ProductFileKind) {
    const productId = editing?.id;
    if (!productId) return;
    const noun = kind === "cover" ? "Cover image" : "Summary PDF";
    setBusyKind(kind);
    const t = toast.loading(`Removing the ${noun.toLowerCase()}…`);
    try {
      const res = await detachProductFile({ data: { productId, kind } });
      qc.invalidateQueries({ queryKey: ["research_products"] });
      if (res.product) setEditing(res.product);
      toast.success(`${noun} removed`, { id: t });
    } catch (e) {
      toast.dismiss(t);
      reportWriteError(e, `Could not remove the ${noun.toLowerCase()}`);
    } finally {
      setBusyKind(null);
    }
  }

  /** Stage the file on our media tree first — Shopify pulls it from there. */
  async function onPickFile(kind: ProductFileKind, file: File | undefined) {
    const ref = kind === "cover" ? coverRef : pdfRef;
    if (!file) return;
    try {
      // The hook toasts every outcome and resolves null on rejection/failure.
      const result = await (kind === "cover" ? coverUpload : pdfUpload).upload(file);
      if (result) await attachAndPoll(kind, `${window.location.origin}${result.url}`);
    } finally {
      if (ref.current) ref.current.value = "";
    }
  }

  const openEdit = (p: ResearchProductAdmin) => {
    setEditing(p);
    seedForm(formOf(p));
    setOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    seedForm({ ...emptyForm });
    setOpen(true);
  };

  const busy = busyKind !== null || coverUpload.busy || pdfUpload.busy;
  const coverWorking = busyKind === "cover" || coverUpload.busy;
  const pdfWorking = busyKind === "pdf" || pdfUpload.busy;
  const dirty = useDirty(form, open);
  const gateTitle = canEdit ? undefined : WAITING_MESSAGE;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Research Reports</CardTitle>
          <CardDescription>
            The catalog behind the public /research page, synced with Shopify.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={(v: SortKey) => setSortBy(v)}>
            <SelectTrigger
              className="h-9 w-44 justify-start gap-2 [&>span]:flex-1 [&>span]:text-left"
              aria-label="Sort reports"
            >
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={statusFilter}
            onValueChange={(v: "ALL" | ProductStatus) => setStatusFilter(v)}
          >
            <SelectTrigger className="h-9 w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All ({counts.ALL})</SelectItem>
              <SelectItem value="ACTIVE">Active ({counts.ACTIVE})</SelectItem>
              <SelectItem value="DRAFT">Draft ({counts.DRAFT})</SelectItem>
              <SelectItem value="ARCHIVED">Archived ({counts.ARCHIVED})</SelectItem>
            </SelectContent>
          </Select>
          <span title={gateTitle}>
            <Button size="sm" variant="outline" disabled={!canEdit} onClick={openNew}>
              <Plus className="mr-1 h-4 w-4" /> New product
            </Button>
          </span>
          <Button size="sm" disabled={syncMut.isPending} onClick={() => syncMut.mutate()}>
            <RefreshCw className={`mr-1 h-4 w-4 ${syncMut.isPending ? "animate-spin" : ""}`} />
            {syncMut.isPending ? "Syncing…" : "Sync from Shopify"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {canEdit ? (
          <div className="mb-4 flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              Shopify stays the source of truth. Saving here writes to Shopify and pulls the result
              straight back into the site cache
              {adminStatus?.shop ? ` (${adminStatus.shop})` : ""}.
            </p>
          </div>
        ) : (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{WAITING_MESSAGE}</p>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Cover</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-28">Price</TableHead>
              <TableHead className="w-28 hidden md:table-cell">Published</TableHead>
              <TableHead className="w-20 hidden md:table-cell">Pages</TableHead>
              <TableHead className="w-24 text-center">Status</TableHead>
              <TableHead className="w-20 text-center">Active</TableHead>
              <TableHead className="w-40 hidden lg:table-cell">Last synced</TableHead>
              <TableHead className="w-20 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((p) => (
              <TableRow key={p.id} className={p.is_active ? "" : "opacity-60"}>
                <TableCell>
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="h-12 w-9 rounded border object-cover" />
                  ) : (
                    <div className="flex h-12 w-9 items-center justify-center rounded border bg-muted text-muted-foreground">
                      <FileText className="h-4 w-4" />
                    </div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell>{formatPrice(p.price_cents, p.currency)}</TableCell>
                <TableCell className="hidden text-muted-foreground md:table-cell">
                  {formatPublishDate(p.publish_date, "long") || "—"}
                </TableCell>
                <TableCell className="hidden text-muted-foreground md:table-cell">
                  {p.pages ?? "—"}
                </TableCell>
                <TableCell className="text-center">
                  <StatusBadge status={p.status} />
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={p.is_active ? "default" : "secondary"}>
                    {p.is_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell className="hidden text-muted-foreground lg:table-cell">
                  {p.synced_at ? new Date(p.synced_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <span title={gateTitle}>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={!canEdit}
                      onClick={() => openEdit(p)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </span>
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  {isLoading
                    ? "Loading…"
                    : rows.length === 0
                      ? "No products yet — run Sync from Shopify."
                      : `No ${statusFilter.toLowerCase()} products.`}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* The media picker is a sibling modal — `busy` keeps its clicks from
          closing this dialog mid-edit. */}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        size="lg"
        icon={FileText}
        title={form.id ? "Edit report" : "New report"}
        context={form.id ? form.title : "New research product (written to Shopify)"}
        dirty={dirty}
        busy={pickerKind !== null || busy || saveMut.isPending}
        asForm
        onSubmit={() => saveMut.mutate(form)}
        footer={
          <>
            {editing && (
              <Button
                type="button"
                variant="destructive"
                className="sm:mr-auto"
                disabled={archiveMut.isPending || busy}
                onClick={() => {
                  if (confirm(`Archive "${editing.title}"? It stays in Shopify but leaves the site.`))
                    archiveMut.mutate(editing.id);
                }}
              >
                {archiveMut.isPending ? "Archiving…" : "Archive product"}
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
            <SaveButton dirty={dirty} pending={saveMut.isPending} disabled={busy} />
          </>
        }
      >
        <div className="space-y-4">
            <FormSection title="Report" cols={2}>
            <Field label="Title" required full>
              <Input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </Field>
            <Field label="Price (USD)" required>
              <Input
                required
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="12500.00"
              />
            </Field>
            <Field label="Status">
              <Select
                value={form.status}
                onValueChange={(v: ProductStatus) => setForm({ ...form, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {/* Month + Year selector (customer decision 2026-09-01): the site
                shows publish dates as Month Year only, so the editor collects
                exactly that. Stored as "YYYY-MM-01" — a real date string, so
                the cache's string sort and any date-typed Shopify metafield
                both stay satisfied. Legacy free-text values that don't parse
                are shown untouched below and only replaced when a month and
                year are picked (an untouched field is never re-sent). */}
            <PublishDateField
              value={form.publish_date}
              onChange={(v) => setForm({ ...form, publish_date: v })}
            />
            <Field label="Pages">
              <Input
                type="number"
                min="0"
                value={form.pages}
                onChange={(e) => setForm({ ...form, pages: e.target.value })}
              />
            </Field>
            </FormSection>
            <FormSection title="Details" cols={2}>
            <Field label="Details (one per line)" full>
              <Textarea
                rows={5}
                value={form.details}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                placeholder={"Market sizing through 2030\nCompetitive share by segment"}
              />
            </Field>
            <Field label="Details description" full>
              <Textarea
                rows={3}
                value={form.details_description}
                onChange={(e) => setForm({ ...form, details_description: e.target.value })}
              />
            </Field>
            <Field label="Description" full>
              <RichTextEditor
                value={form.description_html}
                onChange={(html) => setForm((f) => ({ ...f, description_html: html }))}
                uploadFolder="research"
                minHeight={180}
              />
            </Field>

            </FormSection>
            <FormSection title="Files" cols={2}>
            <Field label="Cover image" full>
              {editing ? (
                <div className="flex items-center gap-3">
                  {editing.image_url ? (
                    <img
                      src={editing.image_url}
                      alt=""
                      className="h-20 w-14 rounded border object-cover"
                    />
                  ) : (
                    <div className="flex h-20 w-14 items-center justify-center rounded border bg-muted text-muted-foreground">
                      <ImageIcon className="h-5 w-5" />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => coverRef.current?.click()}
                  >
                    <Upload className="mr-1 h-3.5 w-3.5" />
                    {coverUpload.busy
                      ? `Uploading… ${coverUpload.percent}%`
                      : coverWorking
                        ? "Working…"
                        : "Upload new"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setPickerKind("cover")}
                  >
                    <Library className="mr-1 h-3.5 w-3.5" /> Choose from library
                  </Button>
                  {editing.image_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        if (confirm("Remove the cover image from this report?"))
                          void detachFile("cover");
                      }}
                    >
                      <X className="mr-1 h-3.5 w-3.5" /> Remove
                    </Button>
                  )}
                  <input
                    ref={coverRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => onPickFile("cover", e.target.files?.[0])}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Save the report first — files attach to an existing Shopify product.
                </p>
              )}
            </Field>

            <Field label="Summary PDF" full>
              {editing ? (
                <div className="flex items-center gap-3">
                  {editing.summary_pdf_url ? (
                    <a
                      href={editing.summary_pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm hover:underline"
                    >
                      <FileText className="h-4 w-4" /> Current summary
                    </a>
                  ) : (
                    <span className="text-sm text-muted-foreground">No summary attached</span>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => pdfRef.current?.click()}
                  >
                    <Upload className="mr-1 h-3.5 w-3.5" />
                    {pdfUpload.busy
                      ? `Uploading… ${pdfUpload.percent}%`
                      : pdfWorking
                        ? "Working…"
                        : "Upload new"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setPickerKind("pdf")}
                  >
                    <Library className="mr-1 h-3.5 w-3.5" /> Choose from library
                  </Button>
                  {editing.summary_pdf_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        if (confirm("Remove the summary PDF from this report?"))
                          void detachFile("pdf");
                      }}
                    >
                      <X className="mr-1 h-3.5 w-3.5" /> Remove
                    </Button>
                  )}
                  <input
                    ref={pdfRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => onPickFile("pdf", e.target.files?.[0])}
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Save the report first — files attach to an existing Shopify product.
                </p>
              )}
            </Field>

            </FormSection>
        </div>
      </FormDialog>

      {/* Library assets already live on the public media tree, so Shopify can
          fetch them straight from the CDN URL. */}
      <MediaPickerDialog
        open={pickerKind !== null}
        onOpenChange={(o) => !o && setPickerKind(null)}
        target={pickerKind === "pdf" ? MEDIA_TARGETS.researchPdf : MEDIA_TARGETS.researchCover}
        browseFolder="all"
        title={pickerKind === "pdf" ? "Choose a summary PDF" : "Choose a cover image"}
        onSelect={({ url }) => {
          const kind = pickerKind;
          setPickerKind(null);
          if (kind) void attachAndPoll(kind, `${window.location.origin}${url}`);
        }}
      />
    </Card>
  );
}

function StatusBadge({ status }: { status?: ProductStatus }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  if (status === "ACTIVE") return <Badge variant="secondary">Active</Badge>;
  if (status === "DRAFT") return <Badge variant="outline">Draft</Badge>;
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Archived
    </Badge>
  );
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** 2015 .. next year, newest first — covers the whole live catalog. */
const PUBLISH_YEARS = (() => {
  const max = new Date().getFullYear() + 1;
  const years: string[] = [];
  for (let y = max; y >= 2015; y--) years.push(String(y));
  return years;
})();

/**
 * Month + Year picker over the stored publish-date string. Picking either half
 * writes "YYYY-MM-01" (the other half defaults to the current year / January,
 * and the selects always show exactly what will be stored). Clear empties the
 * field, which travels as "" and clears the metafield on save.
 */
function PublishDateField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const parts = /^(\d{4})-(\d{1,2})/.exec(value.trim());
  const month = parts && Number(parts[2]) >= 1 && Number(parts[2]) <= 12 ? parts[2].padStart(2, "0") : "";
  const year = parts ? parts[1] : "";
  const isLegacyText = value.trim() !== "" && !parts;
  const set = (mm: string, yyyy: string) => onChange(`${yyyy}-${mm}-01`);
  return (
    <Field label="Published (month and year)">
      <div className="flex items-center gap-2">
        <Select
          value={month}
          onValueChange={(v) => set(v, year || String(new Date().getFullYear()))}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((name, i) => (
              <SelectItem key={name} value={String(i + 1).padStart(2, "0")}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={(v) => set(month || "01", v)}>
          <SelectTrigger className="w-24">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {PUBLISH_YEARS.map((y) => (
              <SelectItem key={y} value={y}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {value.trim() !== "" && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")}>
            Clear
          </Button>
        )}
      </div>
      {isLegacyText && (
        <p className="text-xs text-muted-foreground">
          Stored as “{value}” — pick a month and year to replace it, or leave it as is.
        </p>
      )}
    </Field>
  );
}

